
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { SuccessHeader } from '@/components/application/submission/SuccessHeader';
import { ExpectationsSection } from '@/components/application/submission/ExpectationsSection';
import { PremiumFeatures } from '@/components/application/submission/PremiumFeatures';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

const ApplicationSubmitted = () => {
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [applicationId, setApplicationId] = useState<string | null>(null);

  // Check for submitted application and verify its status
  useEffect(() => {
    const checkSubmissionStatus = async () => {
      setIsVerifying(true);
      
      // Get application ID from session storage
      const submittedAppId = sessionStorage.getItem('submittedApplicationId');
      const submissionFlag = sessionStorage.getItem('applicationSubmitted');
      
      if (!submissionFlag || !submittedAppId) {
        console.log('No application submission data found in session storage');
        navigate('/application');
        return;
      }
      
      try {
        // Verify that the application exists and is marked as submitted
        const { data, error } = await supabase
          .from('applications')
          .select('id, status, company_legal_name')
          .eq('id', submittedAppId)
          .single();
          
        if (error || !data) {
          console.error('Error verifying application:', error);
          navigate('/application');
          return;
        }
        
        console.log('Verified application:', data);
        
        if (data.status !== 'submitted') {
          console.log('Application exists but status is not submitted:', data.status);
          
          // Force update the status if needed
          const { error: updateError } = await supabase
            .from('applications')
            .update({ status: 'submitted' })
            .eq('id', submittedAppId);
            
          if (updateError) {
            console.error('Failed to update application status:', updateError);
          } else {
            console.log('Successfully updated application status to submitted');
          }
        }
        
        // Store the application ID for reference
        setApplicationId(submittedAppId);
      } catch (err) {
        console.error('Error in submission verification:', err);
        navigate('/application');
      } finally {
        setIsVerifying(false);
      }
    };
    
    checkSubmissionStatus();
  }, [navigate]);

  // Handler for returning to dashboard with proper state
  const handleReturnToDashboard = () => {
    // Set location state to indicate we're coming from submission
    navigate('/dashboard', { state: { fromSubmission: true } });
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-lg">Verifying submission status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-3xl space-y-8">
        {/* Success Message */}
        <SuccessHeader />

        {/* Main Content Card */}
        <Card className="shadow-lg border-0">
          <CardContent className="space-y-8 pt-6">
            <ExpectationsSection />

            {/* Premium Features Upsell */}
            <PremiumFeatures />
          </CardContent>
          <CardFooter className="flex justify-between items-center">
            <Button 
              variant="outline" 
              className="border-gray-200 hover:bg-gray-50"
              onClick={handleReturnToDashboard}
            >
              Return to Dashboard
            </Button>
            <Button
              variant="outline"
              className="border-gray-200 hover:bg-gray-50"
              onClick={() => window.location.href = 'mailto:support@thepeoapp.com'}
            >
              Contact Support
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationSubmitted;
